import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { posWoltService } from '../../../../services/pos-wolt/pos-wolt.service';

type Params = {
  tenantId: string;
  employeeSession: string | null;
  opened: boolean;
};

// Fetches the venue status once per open (no polling); one in-flight toggle at a time.
export function useWoltVenueController({ tenantId, employeeSession, opened }: Params) {
  const [online, setOnline] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveLockRef = useRef(false);

  useEffect(() => {
    if (!opened || !employeeSession) {
      setOnline(null);
      setError(null);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    posWoltService
      .getVenueStatus(tenantId, employeeSession)
      .then((status) => {
        if (active) setOnline(status.online);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'קריאת סטטוס הסניף בוולט נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [opened, tenantId, employeeSession]);

  const setVenueOnline = useCallback(async (next: boolean): Promise<boolean> => {
    if (!employeeSession || saveLockRef.current) {
      return false;
    }
    saveLockRef.current = true;
    setIsSaving(true);
    try {
      const status = await posWoltService.setVenueOnline(tenantId, employeeSession, next);
      setOnline(status.online);
      notifications.show({ color: 'green', title: 'וולט', message: status.online ? 'הסניף פתוח בוולט' : 'הסניף סגור בוולט', autoClose: 3000 });
      return true;
    } catch (saveError: unknown) {
      notifications.show({
        color: 'red',
        title: 'וולט',
        message: saveError instanceof Error ? saveError.message : 'עדכון סטטוס הסניף בוולט נכשל',
        autoClose: 4000,
      });
      return false;
    } finally {
      saveLockRef.current = false;
      setIsSaving(false);
    }
  }, [employeeSession, tenantId]);

  return { online, isLoading, isSaving, error, setVenueOnline };
}
